import "babel-regenerator-runtime"
import React from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { BrowserRouter, Route, Routes } from "react-router-dom"
import { App as AppStyles } from "@bdfx/mergado-ui-kit"
import store from "./store"
import Style from "./load_styles"
import { Global, Theme } from '@emotion/react'

import App from "app/components/app"
import Widget from 'app/components/widget'
import Authenticated from "app/components/authenticated"
import NotFound from "app/components/not-found"

import Loadable from "react-loadable"
import trans from "trans";
import { getImagickFonts } from "./app/helpers/api";
import { APP_MAINTENANCE_MODE, REPLAY_RATIO, SENTRY_DSN_JS, URL } from "./app/config/api-config";
import { ImagickFont } from "./shape-edit/components/imagickFontSelect";
import { setImagickFontsToStore } from "./app/reducers/imagickFontsReduxList";
import * as Sentry from '@sentry/browser';

import { AxiosResponse } from "axios";
import { Interpolation } from "@emotion/serialize";
import { getToken } from "app/helpers/session"
import Maintenance from "app/components/maintenance"

const Placeholder = () => <div style={{ width: '100%', textAlign: 'center' }}>{trans('loading')}</div>

const SetCookie = Loadable({
    loader: () => import('app/set-cookie'),
    loading: Placeholder,
});

const isWidget: boolean = (window.location.pathname.substr(1, 6) === 'widget' || window.location.pathname === '/oauth2')

const RootRoutes = () => {
    if (APP_MAINTENANCE_MODE === '1') {
        return <Routes>
            <Route path="*" element={<Maintenance />} />
        </Routes>
    }
    return <Routes>
        <Route path="/widget/eshop/:eshop_id/project/:project_id" element={<Widget />} />
        <Route path="/eshop/:eshop_id/project/:project_id/*" element={<App />} />
        <Route path="/project/:project_id/set-cookie/:fake_project_id" element={<SetCookie />} />
        <Route path="/oauth2" element={<Authenticated />} />
        <Route path="*" element={<NotFound />} />
    </Routes>
}

const rootElement = document.getElementById('app')
const root = createRoot(rootElement)

function AppWithStyles({ callback, styles }) {
    // Callback will be called when the div is first created.
    return <AppStyles>
        <BrowserRouter>
            <Global styles={styles as Interpolation<Theme>} />
            <Provider store={store}>
                <RootRoutes />
            </Provider>
        </BrowserRouter>
    </AppStyles>
}

if (!isWidget) {
    getImagickFonts()
        .then((response: AxiosResponse<any>) => {
            const imagickFonts = response.data.fontsMap
            store.dispatch(setImagickFontsToStore(response.data))

            const addFonts = imagickFonts.map((o: ImagickFont) => ({
                "@font-face": {
                    fontFamily: o.text_fontfamily,
                    fontWeight: o.weight ? o.weight : undefined,
                    fontStretch: o.stretch ? o.stretch : undefined,
                    fontSynthesis: 'none',
                    src: `local("${o.text_fontfamily}"),url("${URL.publicPath}fie-fonts/${o.file}"),local("Unicode-BMP-Fallback-SIL")`,
                    /* ascentOverride: '90%',
                    descentOverride: '22%',
                    lineGapOverride: '0%', */
                    /* 'Pepi-Bold-Bold', 'Pepi-Medium-Regular', 'Pepi-Regular-Regular', 'Pepi-SemiBold' */
                },
            }))

            const NewStyle = { ...Style, ...addFonts }
            root.render(<AppWithStyles callback={() => console.debug("renderered")} styles={NewStyle} />)
        })
        .catch(error => {
            console.debug('error while loading elements', error)
        })
}

if (SENTRY_DSN_JS && SENTRY_DSN_JS !== '') {
    const token = getToken()
    Sentry.init({
        dsn: `${SENTRY_DSN_JS}`,
        environment: 'js',
        replaysSessionSampleRate: REPLAY_RATIO, // This sets the sample rate to be 10%.
        replaysOnErrorSampleRate: 1.0, // If the entire session is not sampled, use the below sample rate to sample sessions when an error occurs

        initialScope: {
            user: {
                id: token.sub,
                locale: token.locale,
            },
        },
        denyUrls: ["widget"],
        integrations: [],
    })
    Sentry.getCurrentScope().setTag('logger', 'javascript');
}



root.render(<AppWithStyles callback={() => console.debug("renderered")} styles={Style} />)
